import { isTicketWarning } from "../../../utils/utils";

export const requiredFields = [
  {
    //present, is a valid moment date, is not before incident start time, is not after the end time
    name: 'startedAt',
    validationFn: (sizeAndWeight, incident) => {
      let { endedAt, startedAt } = sizeAndWeight || {};
      let { callTime } = incident || {};
      if (endedAt === null || endedAt === undefined || startedAt === null || startedAt === undefined || callTime === null || callTime === undefined) {
        return false;
      }
      endedAt = new Date(endedAt);
      startedAt = new Date(startedAt);
      callTime = new Date(callTime);

      //set seconds and milliseconds to 0 to make the comparison only in seconds. "Rounding" down to avoid validation errors for times that look the same
      endedAt.setSeconds(0, 0); 
      startedAt.setSeconds(0, 0); 
      callTime.setSeconds(0, 0); 

      return !!(endedAt >= startedAt && startedAt >= callTime);
    },
    error: 'The start time must be the same or after the incident call time and before the weigh end time'
  },
  {
    //present (does not have to be a valid address)
    name: 'location',
    validationFn: (sizeAndWeight) => !!sizeAndWeight?.location?.name,
    error: 'Location is required'
  },
  {
    // roadType: must be present
    name: 'roadType',
    validationFn: (sizeAndWeight) => !!sizeAndWeight?.roadType?.id,
    error: 'Road Type is required'
  },
  {
    // stateHighway: must be present if roadType is not Municipal
    name: 'stateHighway',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.roadType?.name !== 'Municipal') {
        return !!sizeAndWeight?.stateHighway?.id
      } else {
        return true
      }
    },
    error: 'State Highway is required',
  },
  {
    // enforcementActivity: must be present
    name: 'enforcementActivity',
    validationFn: (sizeAndWeight) => !!sizeAndWeight?.enforcementActivity?.id,
    error: 'Enforcement Activity is required'
  },
  {
    // weightMethod: at least one value must be selected
    name: 'weightMethods',
    validationFn: (sizeAndWeight) => !!(sizeAndWeight?.weightMethods?.length > 0),
    error: 'Weight Method is required'
  },
  {
    // powerUnit: at least one value must be selected
    name: 'powerUnit',
    validationFn: (sizeAndWeight) => !!(sizeAndWeight?.powerUnit?.id),
    error: 'A power unit must be selected'
  },
  {
    // commodity: at least one value must be selected
    name: 'commodities',
    validationFn: (sizeAndWeight) => !!(sizeAndWeight?.commodities?.length > 0),
    error: 'Commodity is required'
  },
  {
    // registeredWeight: must be present, zero is valid
    name: 'registeredWeight',
    validationFn: (sizeAndWeight) => {
      const { registeredWeight } = sizeAndWeight || {};
      return !!(Number.isInteger(registeredWeight) && registeredWeight >= 0)
    },
    error: 'Registered Weight is required'
  },
  {
    // roadLimitWeight: must be present, zero is valid
    name: 'roadLimitWeight',
    validationFn: (sizeAndWeight) => {
      const { roadLimitWeight } = sizeAndWeight || {};
      return !!(Number.isInteger(roadLimitWeight) && roadLimitWeight >= 0)
    },
    error: 'Road Limit Weight is required'
  },
  {
    // steerAxleTireSize: must be present
    name: 'steerAxleTireSize',
    validationFn: (sizeAndWeight) => {
      const { steerAxleTireSize } = sizeAndWeight || {};
      return steerAxleTireSize !== null && steerAxleTireSize !== undefined && !isNaN(steerAxleTireSize) && steerAxleTireSize >= 0;
    },
    error: 'Steer Axle Tire Size is required'
  },
  {
    // axleGroupType: must present for each axle group
    name: 'axleGroupType',
    validationFn: (sizeAndWeight) => !!sizeAndWeight?.axleGroups?.every(axleGroup => !!axleGroup?.axleGroupType?.id),
    error: 'Axle Group Type is required'
  },
  {
    // axleGroupLimit: must be present for each axleGroup, zero is valid
    name: 'axleGroupLimit',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.platformWeigh) return true
      return !!sizeAndWeight?.axleGroups?.every(axleGroup => Number.isInteger(axleGroup?.axleGroupLimit) && axleGroup?.axleGroupLimit >= 0)
    },
    error: 'Axle Group Limit is required'
  },
  // For Axle Weighing:
  {
    // axles: some value must be selected for each axleGroup in the axles array
    name: 'axles',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.platformWeigh) return true
      return !!sizeAndWeight?.axleGroups?.some(axleGroup => axleGroup?.axles?.length > 0)
    },
    error: 'At least one axle must be selected'
  },
  {
    // leftAxleWeight: must be present, zero is valid (on axle under axleGroups)
    name: 'leftAxleWeight',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.platformWeigh) return true
      return !!sizeAndWeight?.axleGroups?.every(axleGroup => axleGroup?.axles?.every(axle => Number.isInteger(axle?.leftAxleWeight) && axle?.leftAxleWeight >= 0))
    },
    error: 'Left Axle Weight is required. Zero is valid.'
  },
  {
    // rightAxleWeight: must be present, zero is valid (on axle under axleGroups)
    name: 'rightAxleWeight',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.platformWeigh) return true
      return !!sizeAndWeight?.axleGroups?.every(axleGroup => axleGroup?.axles?.every(axle => Number.isInteger(axle?.rightAxleWeight) && axle?.rightAxleWeight >= 0))
    },
    error: 'Right Axle Weight is required. Zero is valid.'
  },
  // For Platform Weighing:
  {
    // weight: must be present, zero is NOT valid (on each AxleGroup)
    name: 'weight',
    validationFn: (sizeAndWeight) => {
      if (!sizeAndWeight?.platformWeigh) return true
      return !!sizeAndWeight?.axleGroups?.every(axleGroup => Number.isInteger(axleGroup?.weight) && axleGroup?.weight > 0)
    },
    error: 'A Platform Weight is required and cannot be 0.'
  },
  // For all Weighing:
  {
    // violationIdentified: must be checked if a Weight Restriction ticket or warning is on the incident
    name: 'violationIdentified',
    validationFn: (sizeAndWeight, incident) => {
      const weightTicket = incident?.incidentTickets?.find(ticket => ticket?.ticketViolationPrototype?.ticketCategory?.name?.includes('Weight Restriction')) || null
      if (!weightTicket) return true
      return !!weightTicket === sizeAndWeight?.violationIdentified
    },
    error: 'There is a weight violation on this incident. This must be checked.'
  },
  {
    // courtAction: must be checked if a Weight Restriction ticket (NOT warning) is on the incident
    name: 'courtAction',
    validationFn: (sizeAndWeight, incident) => {
      const weightTicket = incident?.incidentTickets?.find(ticket => ticket?.ticketViolationPrototype?.ticketCategory?.name?.includes('Weight Restriction')) || null
      if (!weightTicket) return true
      return isTicketWarning(weightTicket?.ticketViolationPrototype) !== sizeAndWeight?.courtAction
    },
    error: 'This box should only be checked if the weight violation is a ticket, not a warning.'
  },
  {
    // scales: must have at least one selected value if the weightMethod is "Portable"
    name: 'scales',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.weightMethods?.some(method => method?.name === 'Portable')) {
        return !!(sizeAndWeight?.scales?.length > 0)
      } else {
        return true
      }
    },
    error: 'Scales are required for a Portable weigh'
  },
  {
    // endedAt: must be present, must be a valid date, cannot be before the incident start date, cannot be before the startedAt date
    name: 'endedAt',
    validationFn: (sizeAndWeight, incident) => {
      let { endedAt, startedAt } = sizeAndWeight || {};
      let { callTime } = incident || {};

      endedAt = new Date(endedAt);
      startedAt = new Date(startedAt);
      callTime = new Date(callTime);

      //set seconds and milliseconds to 0 to make the comparison only in seconds. "Rounding" down to avoid validation errors for times that look the same
      endedAt.setSeconds(0, 0); 
      startedAt.setSeconds(0, 0); 
      callTime.setSeconds(0, 0); 

      if (endedAt === null || endedAt === undefined || startedAt === null || startedAt === undefined || callTime === null || callTime === undefined) {
        return false;
      }
      return (new Date(endedAt) >= new Date(startedAt)) && (new Date(endedAt) >= new Date(callTime));
    },
    error: 'The end time must be after the start time and after the incident start time'
  },
  {
    name: 'printCarrier',
    validationFn: (sizeAndWeight) => {
      if (!sizeAndWeight?.printCarrier && !sizeAndWeight?.printOwner) {
        return false
      } else {
        return true
      }
    },
    error: 'Either the Carrier or Owner must be selected for printing'
  },
  {
    name: 'printOwner',
    validationFn: (sizeAndWeight) => {
      if (!sizeAndWeight?.printCarrier && !sizeAndWeight?.printOwner) {
        return false
      } else {
        return true
      }
    },
    error: 'Either the Carrier or Owner must be selected for printing'
  },
  {
    name: 'carrier',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.printCarrier && !sizeAndWeight?.carrier?.personalAttribute?.lastName) {
        return false
      } else {
        return true
      }
    },
    error: 'The carrier cannot be blank'
  },
  {
    name: 'owner',
    validationFn: (sizeAndWeight) => {
      if (sizeAndWeight?.printOwner && !sizeAndWeight?.owner?.personalAttribute?.lastName) {
        return false
      } else {
        return true
      }
    },
    error: 'The owner cannot be blank'
  },
]

export function validateState(state, incident) {
  let invalidFields = [];

  requiredFields.forEach(field => {
    const { validationFn } = field;
    if (!validationFn(state, incident)) {
      invalidFields.push(field);
    }
  })

  return (
    {
      valid: invalidFields.length === 0,
      invalidFields: invalidFields
    }
  )
}